// calculates font size clamp using formula from:
// https://www.smashingmagazine.com/2022/01/modern-fluid-typography-css-clamp/
const buildFontSizeClamp = (min: number, max: number): string => {
  // values in rem - equal 640 and 1224px
  const minBreakpoint = 40;
  const maxBreakPoint = 76.5;
  const v = (100 * (max - min)) / (maxBreakPoint - minBreakpoint);
  const r = (minBreakpoint * max - maxBreakPoint * min) / (minBreakpoint - maxBreakPoint);
  return `clamp(${min}rem, ${v}vw + ${r}rem, ${max}rem)`;
};
type BaseTheme = {
  colors: {
    transparent: 'transparent';
    white: {
      5: '#E1E2E3';
      15: '#F0F0F1';
      50: '#F7F7F7';
      75: '#FEF9F9';
      100: '#FFFFFF';
    };
    red: {
      100: '#F14157';
      75: '#EC4040';
      50: '#F38080';
      25: '#F9BFBF';
      0: '#FFDCDC';
    };
    black: {
      full: '#0B0B0C';
      100: '#4A4D4E';
      75: '#777A7A';
      50: '#0B0B0C';
      25: '#D2D2D3';
    };
    grey: {
      100: '#A8A9AE';
    };
    'red-violet': {
      100: '#9C2AA0';
      75: '#B55FB8';
      50: '#CD94CF';
      25: '#E6CAE7';
    };
    aubergine: {
      100: '#5E2750';
      75: '#865D7C';
      50: '#AE93A7';
      25: '#D7C9D3';
    };
    green: {
      100: '#A8B400';
      75: '#BEC740';
      50: '#D3D980';
      25: '#E9ECBF';
    };
    orange: {
      100: '#EB9700';
      75: '#F0B140';
      50: '#F5CB80';
      25: '#FAE5BF';
    };
    primary: {
      red: '#F14157';
      'deep-red': '#570E20';
      'dark-red': '#BE0E23';
      'light-coral': '#F57A89';
      stone: '#FBDBE0';
      'surface-high': '#E8E8E8';
      'surface-low': '#F7F7F7';
      cloud: '#DFDEDC';
      white: '#FFFFFF';
      black: '#1E1E1C';
      slate: '#2F2F30';
      sea: '#3A5D64';
      'deep-sea': '#1C2E32';
      'light-blue': '#B0CCD2';
      'light-green': '#D3E3E5';
      disabled: '#D8D8DA';
      'disabled-text': '#8F8F90';
      'disabled-alt-text': '#9E9E9F';
      input: '#F2F2F280';
      'input-full': '#FCFCFC';
      'faded-text': '#50505780';
      'faded-text-100': '#505057';
      //old
      grey: '#DFDEDC';
      'slate-grey': '#25282B';
      inverse: {
        red: '#EA1A1A';
        white: '#FFFFFF';
        grey: '#4A4D4E';
        'slate-grey': '#25282B';
      };
      //
    };
    plus: {
      red: '#F14157';
      grape: '#653089';
      blue: '#686CB0';
    };
    sport: {
      red: '#F14157';
      orange: '#EE7330';
      yellow: '#F8C88F';
    };
    neutral: {
      10: '#181818';
    };
    button: {
      primary: {
        default: '#F14157';
        hover: '#ED3D53';
        pressed: '#ED3D53';
        focus: '#EB3B51';
        overlay: '#BE0E23';
      };
      secondary: {
        default: '#3A5D64';
        hover: '#45676F';
        pressed: '#45676F';
        focus: '#335964';
        overlay: '#003A65';
      };
      tertiary: {
        default: '#00A892';
        hover: '#00A28C';
        pressed: '#00A38D';
        focus: '#00A08B';
        overlay: '#006558';
      };
      surface: {
        default: '#F7F7F7';
        hover: '#F7E8EA';
        pressed: '#F7E8EA';
        focus: '#F6E1E4';
        overlay: '#F14157';
      };
    };
    state: {
      error: '#BD0000';
      warning: '#EB6100';
      success: '#00A892';
      info: '#005EA5';
      focus: '#0096AD';
      incomplete: '#BEBEBE';
      inverse: {
        error: '#F06666';
        warning: '#EB9700';
        success: '#00A892';
        info: '#5F9BC8';
        focus: '#00B0CA';
        incomplete: '#7E7E7E';
      };
    };
    monochrome: {
      1: '#FFFFFF';
      2: '#F2F2F2';
      3: '#BEBEBE';
      4: '#7E7E7E';
      5: '#262626';
      6: '#0D0D0D';
      inverse: {
        1: '#0D0D0D';
        2: '#262626';
        3: '#7E7E7E';
        4: '#BEBEBE';
        5: '#F2F2F2';
        6: '#FFFFFF';
      };
    };
  };
  oldColors: {
    // Colors from original design by Oddur
    transparent: 'transparent';
    red: {
      100: '#E60000';
      75: '#EC4040';
      50: '#F38080';
      25: '#F9BFBF';
      0: '#FFDCDC';
    };
    black: {
      100: '#4A4D4E';
      75: '#777A7A';
      50: '#A4A6A6';
      25: '#D2D2D3';
    };
    white: {
      50: '#F4F4F4';
      15: '#f0f0f1';
      100: '#FFFFFF';
    };
    'red-violet': {
      100: '#9C2AA0';
      75: '#B55FB8';
      50: '#CD94CF';
      25: '#E6CAE7';
    };
    aubergine: {
      100: '#5E2750';
      75: '#865D7C';
      50: '#AE93A7';
      25: '#D7C9D3';
    };
    green: {
      100: '#A8B400';
      75: '#BEC740';
      50: '#D3D980';
      25: '#E9ECBF';
    };
    orange: {
      100: '#EB9700';
      75: '#F0B140';
      50: '#F5CB80';
      25: '#FAE5BF';
    };
    // Colors from Vodafone International
    primary: {
      red: '#E60000';
      white: '#FFFFFF';
      grey: '#4A4D4E';
      'slate-grey': '#25282B';
      inverse: {
        red: '#EA1A1A';
        white: '#FFFFFF';
        grey: '#4A4D4E';
        'slate-grey': '#25282B';
      };
    };
    secondary: {
      turquoise: '#00697C';
      aubergine: '#5E2750';
      orange: '#EB6100';
      yellow: '#FECB00';
      green: '#008A00';
      'red-violet': '#9C2AA0';
      red: '#BD0000';
      inverse: {
        turquoise: '#0096AD';
        aubergine: '#B04A98';
        orange: '#EB9700';
        yellow: '#FECB00';
        green: '#B7BF10';
        'red-violet': '#D162D5';
        red: '#F06666';
      };
    };
    state: {
      error: '#BD0000';
      warning: '#EB6100';
      success: '#00A892';
      info: '#005EA5';
      focus: '#0096AD';
      incomplete: '#BEBEBE';
      inverse: {
        error: '#F06666';
        warning: '#EB9700';
        success: '#00A892';
        info: '#5F9BC8';
        focus: '#00B0CA';
        incomplete: '#7E7E7E';
      };
    };
    monochrome: {
      1: '#FFFFFF';
      2: '#F2F2F2';
      3: '#BEBEBE';
      4: '#7E7E7E';
      5: '#262626';
      6: '#0D0D0D';
      inverse: {
        1: '#0D0D0D';
        2: '#262626';
        3: '#7E7E7E';
        4: '#BEBEBE';
        5: '#F2F2F2';
        6: '#FFFFFF';
      };
    };
    button: {
      primary: {
        default: '#E60000';
        hover: '#C30000';
        pressed: '#A10000';
      };
      secondary: {
        default: '#0D0D0D';
        hover: '#313131';
        pressed: '#565656';
      };
      tertiary: {
        default: '#FFFFFF';
        hover: '#313131';
        pressed: '#565656';
      };
      success: {
        default: '#008A00';
        hover: '#007500';
        pressed: '#006100';
      };
    };
  };
  spacing: {
    0: '0rem';
    1: '0.0625rem';
    2: '0.125rem';
    4: '0.25rem';
    8: '0.5rem';
    12: '0.75rem';
    16: '1rem';
    24: '1.5rem';
    32: '2rem';
    40: '2.5rem';
    48: '3rem';
    56: '3.5rem';
    64: '4rem';
    80: '5rem';
    100: '6rem';
    128: '8rem';
    160: '10rem';
    '2xs': '0.125rem';
    xs: '0.25rem';
    sm: '0.5rem';
    medium: '0.75rem';
    lg: '1rem';
    xl: '1.5rem';
    '2xl': '1.75rem';
    '3xl': '2rem';
    big: '3rem';
    xbig: '3.5rem';
    full: '1000px';
  };
  boxShadows: {
    sm: '0px 0px 4px 0px rgba(0, 0, 0, 0.24)';
    md: '0px 2px 4px 0px rgba(0, 0, 0, 0.24)';
    lg: '0px 4px 8px 0px rgba(0, 0, 0, 0.24)';
    xl: '0px 8px 16px 0px rgba(0, 0, 0, 0.24)';
    card: '0px 0px 16px 0px rgb(0, 0, 0, 0.16)';
  };
  fontSizes: {
    sm: string;
    'sm-fixed': string;
    base: string;
    'base-fixed': string;
    md: string;
    'md-fixed': string;
    md2: string;
    'md2-fixed': string;
    lg: string;
    'lg-fixed': string;
    xl: string;
    'xl-fixed': string;
    '2xl': string;
    '2xl-fixed': string;
    '3xl': string;
    '3xl-fixed': string;
    '4xl': string;
    '4xl-fixed': string;
    '5xl': string;
    '5xl-fixed': string;
  };
  breakpoints: {
    xl: '1224px';
  };
};

const baseTheme: BaseTheme = {
  colors: {
    transparent: 'transparent',
    red: {
      100: '#F14157',
      75: '#EC4040',
      50: '#F38080',
      25: '#F9BFBF',
      0: '#FFDCDC',
    },
    white: {
      5: '#E1E2E3',
      15: '#F0F0F1',
      50: '#F7F7F7',
      75: '#FEF9F9',
      100: '#FFFFFF',
    },
    // old
    black: {
      full: '#0B0B0C',
      100: '#4A4D4E',
      75: '#777A7A',
      50: '#0B0B0C',
      25: '#D2D2D3',
    },
    grey: {
      100: '#A8A9AE',
    },
    'red-violet': {
      100: '#9C2AA0',
      75: '#B55FB8',
      50: '#CD94CF',
      25: '#E6CAE7',
    },
    aubergine: {
      100: '#5E2750',
      75: '#865D7C',
      50: '#AE93A7',
      25: '#D7C9D3',
    },
    green: {
      100: '#A8B400',
      75: '#BEC740',
      50: '#D3D980',
      25: '#E9ECBF',
    },
    orange: {
      100: '#EB9700',
      75: '#F0B140',
      50: '#F5CB80',
      25: '#FAE5BF',
    },
    // new
    primary: {
      red: '#F14157',
      'deep-red': '#570E20',
      'dark-red': '#BE0E23',
      'light-coral': '#F57A89',
      stone: '#FBDBE0',
      'surface-high': '#E8E8E8',
      'surface-low': '#F7F7F7',
      cloud: '#DFDEDC',
      white: '#FFFFFF',
      black: '#1E1E1C',
      slate: '#2F2F30',
      sea: '#3A5D64',
      'deep-sea': '#1C2E32',
      'light-blue': '#B0CCD2',
      'light-green': '#D3E3E5',
      disabled: '#D8D8DA',
      'disabled-text': '#8F8F90',
      'disabled-alt-text': '#9E9E9F',
      'faded-text': '#50505780',
      'faded-text-100': '#505057',
      grey: '#DFDEDC',
      input: '#F2F2F280',
      'input-full': '#FCFCFC',
      //old
      'slate-grey': '#25282B',
      inverse: {
        red: '#EA1A1A',
        white: '#FFFFFF',
        grey: '#4A4D4E',
        'slate-grey': '#25282B',
      },
      //
    },
    plus: {
      red: '#F14157',
      grape: '#653089',
      blue: '#686CB0',
    },
    sport: {
      red: '#F14157',
      orange: '#EE7330',
      yellow: '#F8C88F',
    },
    neutral: {
      10: '#181818',
    },
    button: {
      primary: {
        default: '#F14157',
        hover: '#ED3D53',
        pressed: '#ED3D53',
        focus: '#EB3B51',
        overlay: '#BE0E23',
      },
      secondary: {
        default: '#3A5D64',
        hover: '#45676F',
        pressed: '#45676F',
        focus: '#335964',
        overlay: '#003A65',
      },
      tertiary: {
        default: '#00A892',
        hover: '#00A28C',
        pressed: '#00A38D',
        focus: '#00A08B',
        overlay: '#006558',
      },
      surface: {
        default: '#F7F7F7',
        hover: '#F7E8EA',
        pressed: '#F7E8EA',
        focus: '#F6E1E4',
        overlay: '#F14157',
      },
    },
    state: {
      error: '#BD0000',
      warning: '#EB6100',
      success: '#00A892',
      info: '#005EA5',
      focus: '#0096AD',
      incomplete: '#BEBEBE',
      inverse: {
        error: '#F06666',
        warning: '#EB9700',
        success: '#00A892',
        info: '#5F9BC8',
        focus: '#00B0CA',
        incomplete: '#7E7E7E',
      },
    },
    monochrome: {
      1: '#FFFFFF',
      2: '#F2F2F2',
      3: '#BEBEBE',
      4: '#7E7E7E',
      5: '#262626',
      6: '#0D0D0D',
      inverse: {
        1: '#0D0D0D',
        2: '#262626',
        3: '#7E7E7E',
        4: '#BEBEBE',
        5: '#F2F2F2',
        6: '#FFFFFF',
      },
    },
  },
  oldColors: {
    transparent: 'transparent',
    red: {
      100: '#E60000',
      75: '#EC4040',
      50: '#F38080',
      25: '#F9BFBF',
      0: '#FFDCDC',
    },
    black: {
      100: '#4A4D4E',
      75: '#777A7A',
      50: '#A4A6A6',
      25: '#D2D2D3',
    },
    white: {
      50: '#F4F4F4',
      15: '#f0f0f1',
      100: '#FFFFFF',
    },
    'red-violet': {
      100: '#9C2AA0',
      75: '#B55FB8',
      50: '#CD94CF',
      25: '#E6CAE7',
    },
    aubergine: {
      100: '#5E2750',
      75: '#865D7C',
      50: '#AE93A7',
      25: '#D7C9D3',
    },
    green: {
      100: '#A8B400',
      75: '#BEC740',
      50: '#D3D980',
      25: '#E9ECBF',
    },
    orange: {
      100: '#EB9700',
      75: '#F0B140',
      50: '#F5CB80',
      25: '#FAE5BF',
    },
    primary: {
      red: '#E60000',
      white: '#FFFFFF',
      grey: '#4A4D4E',
      'slate-grey': '#25282B',
      inverse: {
        red: '#EA1A1A',
        white: '#FFFFFF',
        grey: '#4A4D4E',
        'slate-grey': '#25282B',
      },
    },
    secondary: {
      turquoise: '#00697C',
      aubergine: '#5E2750',
      orange: '#EB6100',
      yellow: '#FECB00',
      green: '#008A00',
      'red-violet': '#9C2AA0',
      red: '#BD0000',
      inverse: {
        turquoise: '#0096AD',
        aubergine: '#B04A98',
        orange: '#EB9700',
        yellow: '#FECB00',
        green: '#B7BF10',
        'red-violet': '#D162D5',
        red: '#F06666',
      },
    },
    state: {
      error: '#BD0000',
      warning: '#EB6100',
      success: '#00A892',
      info: '#005EA5',
      focus: '#0096AD',
      incomplete: '#BEBEBE',
      inverse: {
        error: '#F06666',
        warning: '#EB9700',
        success: '#00A892',
        info: '#5F9BC8',
        focus: '#00B0CA',
        incomplete: '#7E7E7E',
      },
    },
    monochrome: {
      1: '#FFFFFF',
      2: '#F2F2F2',
      3: '#BEBEBE',
      4: '#7E7E7E',
      5: '#262626',
      6: '#0D0D0D',
      inverse: {
        1: '#0D0D0D',
        2: '#262626',
        3: '#7E7E7E',
        4: '#BEBEBE',
        5: '#F2F2F2',
        6: '#FFFFFF',
      },
    },
    button: {
      primary: {
        default: '#E60000',
        hover: '#C30000',
        pressed: '#A10000',
      },
      secondary: {
        default: '#0D0D0D',
        hover: '#313131',
        pressed: '#565656',
      },
      tertiary: {
        default: '#FFFFFF',
        hover: '#313131',
        pressed: '#565656',
      },
      success: {
        default: '#008A00',
        hover: '#007500',
        pressed: '#006100',
      },
    },
  },
  spacing: {
    0: '0rem',
    1: '0.0625rem',
    2: '0.125rem',
    4: '0.25rem',
    8: '0.5rem',
    12: '0.75rem',
    16: '1rem',
    24: '1.5rem',
    32: '2rem',
    40: '2.5rem',
    48: '3rem',
    56: '3.5rem',
    64: '4rem',
    80: '5rem',
    100: '6rem',
    128: '8rem',
    160: '10rem',
    '2xs': '0.125rem',
    xs: '0.25rem',
    sm: '0.5rem',
    medium: '0.75rem',
    lg: '1rem',
    xl: '1.5rem',
    '2xl': '1.75rem',
    '3xl': '2rem',
    big: '3rem',
    xbig: '3.5rem',
    full: '1000px',
  },
  boxShadows: {
    sm: '0px 0px 4px 0px rgba(0, 0, 0, 0.24)',
    md: '0px 2px 4px 0px rgba(0, 0, 0, 0.24)',
    lg: '0px 4px 8px 0px rgba(0, 0, 0, 0.24)',
    xl: '0px 8px 16px 0px rgba(0, 0, 0, 0.24)',
    card: '0px 0px 16px 0px rgb(0, 0, 0, 0.16)',
  },
  fontSizes: {
    sm: buildFontSizeClamp(0.75, 0.875),
    'sm-fixed': '0.875rem',
    base: buildFontSizeClamp(0.875, 1),
    'base-fixed': '1rem',
    md: buildFontSizeClamp(1, 1.125),
    'md-fixed': '1.125rem',
    md2: buildFontSizeClamp(1, 1.25),
    'md2-fixed': '1.25rem',
    lg: buildFontSizeClamp(1.125, 1.5),
    'lg-fixed': '1.5rem',
    xl: buildFontSizeClamp(1.25, 2),
    'xl-fixed': '2rem',
    '2xl': buildFontSizeClamp(1.5, 2.5),
    '2xl-fixed': '2.5rem',
    '3xl': buildFontSizeClamp(1.75, 3.5),
    '3xl-fixed': '3.5rem',
    '4xl': buildFontSizeClamp(2, 4.5),
    '4xl-fixed': '4.5rem',
    '5xl': buildFontSizeClamp(2.5, 6),
    '5xl-fixed': '6rem',
  },
  breakpoints: {
    xl: '1224px',
  },
};

export default baseTheme;
